// This file is auto-generated by @hey-api/openapi-ts

import {
  createClient,
  createConfig,
  type Options,
} from "@hey-api/client-fetch";
import type {
  GetHealthError,
  GetHealthResponse,
  GetReadyError,
  GetReadyResponse,
  PostWebhooksSmartparkData,
  PostWebhooksSmartparkError,
  PostWebhooksSmartparkResponse,
  GetApiGamesData,
  GetApiGamesError,
  GetApiGamesResponse,
  PostApiGamesData,
  PostApiGamesError,
  PostApiGamesResponse,
  PatchApiGamesByGameIdData,
  PatchApiGamesByGameIdError,
  PatchApiGamesByGameIdResponse,
  GetApiGamesByGameIdData,
  GetApiGamesByGameIdError,
  GetApiGamesByGameIdResponse,
  GetApiGamesByGameIdFullData,
  GetApiGamesByGameIdFullError,
  GetApiGamesByGameIdFullResponse,
  GetApiGamesByGameIdPlateAppearancesData,
  GetApiGamesByGameIdPlateAppearancesError,
  GetApiGamesByGameIdPlateAppearancesResponse,
  GetApiGamesByGameIdActivitiesData,
  GetApiGamesByGameIdActivitiesError,
  GetApiGamesByGameIdActivitiesResponse,
  GetApiGamesByGameIdActivitiesByActivityIdData,
  GetApiGamesByGameIdActivitiesByActivityIdError,
  GetApiGamesByGameIdActivitiesByActivityIdResponse,
  GetApiGamesByGameIdActivitiesByActivityIdHitterData,
  GetApiGamesByGameIdActivitiesByActivityIdHitterError,
  GetApiGamesByGameIdActivitiesByActivityIdHitterResponse,
  GetApiActivitiesByActivityIdData,
  GetApiActivitiesByActivityIdError,
  GetApiActivitiesByActivityIdResponse,
  GetApiActivitiesByActivityIdHitterData,
  GetApiActivitiesByActivityIdHitterError,
  GetApiActivitiesByActivityIdHitterResponse,
  PostApiActivitiesByActivityIdObservationsData,
  PostApiActivitiesByActivityIdObservationsError,
  PostApiActivitiesByActivityIdObservationsResponse,
  PutApiActivitiesByActivityIdPlayersData,
  PutApiActivitiesByActivityIdPlayersError,
  PutApiActivitiesByActivityIdPlayersResponse,
  GetApiGamesByGameIdLineupData,
  GetApiGamesByGameIdLineupError,
  GetApiGamesByGameIdLineupResponse,
  PostApiGamesByGameIdLineupData,
  PostApiGamesByGameIdLineupError,
  PostApiGamesByGameIdLineupResponse,
  PutApiGamesByGameIdLineupBySideData,
  PutApiGamesByGameIdLineupBySideError,
  PutApiGamesByGameIdLineupBySideResponse,
  PostApiGamesByGameIdSubstitutionsData,
  PostApiGamesByGameIdSubstitutionsError,
  PostApiGamesByGameIdSubstitutionsResponse,
  PostApiGamesByGameIdEndData,
  PostApiGamesByGameIdEndError,
  PostApiGamesByGameIdEndResponse,
  PostApiGamesByGameIdUnendData,
  PostApiGamesByGameIdUnendError,
  PostApiGamesByGameIdUnendResponse,
  PostApiGamesByGameIdTeamSwapData,
  PostApiGamesByGameIdTeamSwapError,
  PostApiGamesByGameIdTeamSwapResponse,
  PostApiGamesByGameIdTeamsValidatedData,
  PostApiGamesByGameIdTeamsValidatedError,
  PostApiGamesByGameIdTeamsValidatedResponse,
  GetApiMachineError,
  GetApiMachineResponse,
} from "./types.gen";

export const client = createClient(createConfig());

/**
 * Liveness probe
 * The process is up. Cheap and dependency-free.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<unknown, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetHealthResponse,
    GetHealthError,
    ThrowOnError
  >({
    ...options,
    url: "/health",
  });
};

/**
 * Readiness probe
 * Reports 503 until the event store is reachable.
 */
export const getReady = <ThrowOnError extends boolean = false>(
  options?: Options<unknown, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetReadyResponse,
    GetReadyError,
    ThrowOnError
  >({
    ...options,
    url: "/ready",
  });
};

/**
 * Ingest a SmartPark broker event (pitch sensor or tag)
 * Accepts SmartPark broker envelopes (batched, single event-bus, or bare
 * array). A sensor payload (`pitch_data`) ingests a pitch; a tag payload
 * (`tag_data`) ingests an observation (pitch tag) or a full-state assertion.
 * Deterministic failures are acked with 2xx so the broker stops retrying.
 *
 */
export const postWebhooksSmartpark = <ThrowOnError extends boolean = false>(
  options: Options<PostWebhooksSmartparkData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostWebhooksSmartparkResponse,
    PostWebhooksSmartparkError,
    ThrowOnError
  >({
    ...options,
    url: "/webhooks/smartpark",
  });
};

/**
 * List games
 * Active games only by default. A game is active when it has not ended and
 * either has no end time or has not reached it — a game past the end time
 * the broker gave it is excluded whether or not it was ever marked ended.
 *
 */
export const getApiGames = <ThrowOnError extends boolean = false>(
  options?: Options<GetApiGamesData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesResponse,
    GetApiGamesError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games",
  });
};

/**
 * Create a game
 * Parallel of provisioning from the broker's `gameStart`: appends `GameCreated` then `RosterLoaded` (the line-ups given, or empty), which makes the game `in-progress`. The machine binds the broker's sensor traffic to the game. A machine that still hosts a live game is a 409 rather than a silent supersede; end that game first. Answers with the id (the read model folds a moment later; fetch it from `GET /api/games/{gameId}`).
 */
export const postApiGames = <ThrowOnError extends boolean = false>(
  options: Options<PostApiGamesData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesResponse,
    PostApiGamesError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games",
  });
};

/**
 * Assert part of the game situation (state tag)
 * Appends `StateTagged` as a partial patch of the situational fields: inning, half, outs, count, score and occupied bases, any subset, plus an optional `reason`. `taggedBy` is the authenticated caller. Terminal values (3 outs, 3 strikes, 4 balls) are transitions the rules derive and are refused; so is any other field of the game. A patch that changes nothing against the projection appends nothing. Optional `Idempotency-Key` header.
 */
export const patchApiGamesByGameId = <ThrowOnError extends boolean = false>(
  options: Options<PatchApiGamesByGameIdData, ThrowOnError>,
) => {
  return (options?.client ?? client).patch<
    PatchApiGamesByGameIdResponse,
    PatchApiGamesByGameIdError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}",
  });
};

/**
 * Get a game's current state
 */
export const getApiGamesByGameId = <ThrowOnError extends boolean = false>(
  options: Options<GetApiGamesByGameIdData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdResponse,
    GetApiGamesByGameIdError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}",
  });
};

/**
 * One-call hydration snapshot of a game
 * Everything a WebSocket client needs to stand where a client that
 * listened from the start stands: `game` and `lineUp` exactly as
 * `game-updated.state` / `line-up-updated.lineUp` carry them, plus the
 * newest page of plate appearances and of activities. `next` holds the
 * path of the next (older) page of each list on its own endpoint, or
 * `null` on the last page. Always page one; `limit` caps both lists.
 *
 */
export const getApiGamesByGameIdFull = <ThrowOnError extends boolean = false>(
  options: Options<GetApiGamesByGameIdFullData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdFullResponse,
    GetApiGamesByGameIdFullError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/full",
  });
};

/**
 * List a game's plate appearances (paged, newest first)
 * The same `PlateAppearance` resource delivered live over the WebSocket.
 * Newest first; follow `next` for older pages.
 *
 */
export const getApiGamesByGameIdPlateAppearances = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetApiGamesByGameIdPlateAppearancesData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdPlateAppearancesResponse,
    GetApiGamesByGameIdPlateAppearancesError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/plate-appearances",
  });
};

/**
 * List a game's activities (one per pitch, tagged or not; paged, newest first)
 * The same `GameActivity` resource delivered live over the WebSocket:
 * every pitch recorded for the game, including untagged ones (`pitch`
 * present, `result` absent), which a tagger needs to see in order to tag.
 * Newest first by the time the pitch was thrown (`pitch.eventTime`);
 * follow `next` for older pages. Tagging a pitch changes its `result`,
 * never its position.
 *
 */
export const getApiGamesByGameIdActivities = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetApiGamesByGameIdActivitiesData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdActivitiesResponse,
    GetApiGamesByGameIdActivitiesError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/activities",
  });
};

/**
 * Get a single activity scoped to a game
 * Returns the activity for a game-activity id, including the tagged hitter
 * at `playerTag.hitter`. An untagged pitch of the game is returned too
 * (`pitch` present, `result` and `playerTag` absent). Callable by a
 * service via HTTP Basic Auth (`app_clients` credential) or by a user via
 * JWT bearer.
 *
 */
export const getApiGamesByGameIdActivitiesByActivityId = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetApiGamesByGameIdActivitiesByActivityIdData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdActivitiesByActivityIdResponse,
    GetApiGamesByGameIdActivitiesByActivityIdError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/activities/{activityId}",
  });
};

/**
 * Who the hitter is for the plate appearance this activity belongs to
 * Resolves the hitter for the plate appearance that a given activity
 * (pitch, keyed by its broker `event_uuid`) belongs to. The answer is the
 * plate appearance's, not the pitch's: a hitter tagged on an earlier or
 * later pitch of the same appearance (or seated due-up ahead of any tag)
 * answers for every pitch in it; the queried pitch itself may be
 * untagged. `plateAppearanceId` is null while the game is not in
 * progress; `hitter` is null when nothing has named or seated one.
 * Callable by a service via HTTP Basic Auth (`app_clients` credential)
 * or by a user via JWT bearer.
 *
 */
export const getApiGamesByGameIdActivitiesByActivityIdHitter = <
  ThrowOnError extends boolean = false,
>(
  options: Options<
    GetApiGamesByGameIdActivitiesByActivityIdHitterData,
    ThrowOnError
  >,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdActivitiesByActivityIdHitterResponse,
    GetApiGamesByGameIdActivitiesByActivityIdHitterError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/activities/{activityId}/hitter",
  });
};

/**
 * Get a single activity by its global id (game-agnostic)
 * Same entity as the game-scoped activity read, addressed by the
 * globally-unique activityId (= broker `event_uuid`) alone; the service
 * resolves the game internally. For callers that hold only the event
 * UUID and no gameId. Callable by a service via HTTP Basic Auth
 * (`app_clients` credential) or by a user via JWT bearer.
 *
 */
export const getApiActivitiesByActivityId = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetApiActivitiesByActivityIdData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiActivitiesByActivityIdResponse,
    GetApiActivitiesByActivityIdError,
    ThrowOnError
  >({
    ...options,
    url: "/api/activities/{activityId}",
  });
};

/**
 * Who the hitter is for an activity's plate appearance (game-agnostic)
 * Same as the game-scoped hitter read, addressed by the globally-unique
 * activityId (= broker `event_uuid`) alone; the service resolves the game
 * internally. For callers that hold only the event UUID and no gameId,
 * such as the SwingAI consumer. Callable by a service via HTTP Basic Auth
 * (`app_clients` credential) or by a user via JWT bearer.
 *
 */
export const getApiActivitiesByActivityIdHitter = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetApiActivitiesByActivityIdHitterData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiActivitiesByActivityIdHitterResponse,
    GetApiActivitiesByActivityIdHitterError,
    ThrowOnError
  >({
    ...options,
    url: "/api/activities/{activityId}/hitter",
  });
};

/**
 * Score a pitch
 * Parallel of the broker's pitch tag. Appends `ObservationRecorded` to the activity's stream, then fans out. Each POST is a new observation: many taggers may score one pitch and the latest wins; a re-tag is another POST; `NotAPitch` disregards the activity as noise (and retracts it if already committed). The body is the outcome and nothing else, in the domain vocabulary: the machine and the plate-crossing time come from the pitch already recorded on the activity, and outs and runs are derived downstream. The service resolves the game from the activity. Callable by a user (the tagger) via JWT bearer or by a service via HTTP Basic Auth; never anonymously.
 */
export const postApiActivitiesByActivityIdObservations = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PostApiActivitiesByActivityIdObservationsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiActivitiesByActivityIdObservationsResponse,
    PostApiActivitiesByActivityIdObservationsError,
    ThrowOnError
  >({
    ...options,
    url: "/api/activities/{activityId}/observations",
  });
};

/**
 * Say who was batting and pitching on a pitch
 * Parallel of the broker's outcome-less player tag. Appends `PlayerTagObserved` to the activity's stream and fans out, which reconciles the assertion against the line-ups (by id, else by the public fields a tag carries) and appends only the derived `SubstitutionOccurred` / `HitterTagged` / `PitcherTagged` to the game. Either or both roles; each named player must name somebody. PUT: the players on a pitch are a fact about it, asserted whole; a byte-identical re-send collapses. The service resolves the game from the activity. Same credentials as `…/observations`.
 */
export const putApiActivitiesByActivityIdPlayers = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PutApiActivitiesByActivityIdPlayersData, ThrowOnError>,
) => {
  return (options?.client ?? client).put<
    PutApiActivitiesByActivityIdPlayersResponse,
    PutApiActivitiesByActivityIdPlayersError,
    ThrowOnError
  >({
    ...options,
    url: "/api/activities/{activityId}/players",
  });
};

/**
 * The game's line-ups
 * The `LineUp` resource (`home`, `away`, `extra`), exactly as the `line-up-updated` WebSocket envelope carries it.
 */
export const getApiGamesByGameIdLineup = <ThrowOnError extends boolean = false>(
  options: Options<GetApiGamesByGameIdLineupData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiGamesByGameIdLineupResponse,
    GetApiGamesByGameIdLineupError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/lineup",
  });
};

/**
 * Change a line-up
 * One change to one line-up: bring a player in, take one out, or both. Appends `SubstitutionOccurred`. `substitute` takes `in` and `out`; `add` takes `in`; `remove` takes `out` and is accepted only on `extra` (a club's slot is changed with a `substitute`). `battingOrder` and `position` apply to the incoming player and are never valid on `extra`. `in` is matched against the named line-up, then `extra`, so a player already held keeps their id and a promotion out of `extra` supersedes their row there; being placed on a club takes a player out of `extra`. An `add` into `extra` of a player some line-up already holds appends nothing. A `substitute` whose `out` names nobody on the line-up is refused (the broker's own substitutions degrade to an add instead). Allowed before first pitch and after the game has ended. An optional `Idempotency-Key` header makes a retried request a no-op.
 */
export const postApiGamesByGameIdLineup = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PostApiGamesByGameIdLineupData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdLineupResponse,
    PostApiGamesByGameIdLineupError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/lineup",
  });
};

/**
 * Replace a club's line-up
 * One club's line-up restated whole: the parallel of the broker's `lineupUpdate`, merged against the line-up held the same way. Appends one `LineUpReplaced` when the merged line-up differs from the one held, and nothing when it does not, so a repeat is a no-op without an `Idempotency-Key`. The rows replace the batting order: a held player in a slot the body no longer lists is off the card. A held row with no slot (the pitcher or catcher) is kept unless the body names someone in its position, so remove a pitcher by listing their replacement with `position: P`. A row is matched to a held player by `playerId`, else by name or jersey number on this club or in `extra`, so a player already held keeps their id; a player the body lists with no `battingOrder` keeps the slot held for them unless another row takes it. Listed players leave `extra`. Allowed before first pitch and after the game has ended.
 */
export const putApiGamesByGameIdLineupBySide = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PutApiGamesByGameIdLineupBySideData, ThrowOnError>,
) => {
  return (options?.client ?? client).put<
    PutApiGamesByGameIdLineupBySideResponse,
    PutApiGamesByGameIdLineupBySideError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/lineup/{side}",
  });
};

/**
 * @deprecated
 * Record a player substitution (older body)
 * The same command as `POST …/lineup` with `action: substitute`, in the event's own field names. Kept for callers written against it; it can only express a substitution, and `team` may be omitted because the departing player says which line-up the change is on.
 */
export const postApiGamesByGameIdSubstitutions = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PostApiGamesByGameIdSubstitutionsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdSubstitutionsResponse,
    PostApiGamesByGameIdSubstitutionsError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/substitutions",
  });
};

/**
 * End a game
 * Appends `GameEnded` with the projection's current score as final; a winner is named only when there is a clear leader. Parallel of the provisioner's inferred `superseded` end, which is not client-suppliable. The end is a mark on the game (`ended: true`), not a lock: every command still applies to an ended game, and `POST .../unend` takes it back. The broker's pitches and tags for an ended game are dropped; its messages that name the game are recorded.
 */
export const postApiGamesByGameIdEnd = <ThrowOnError extends boolean = false>(
  options: Options<PostApiGamesByGameIdEndData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdEndResponse,
    PostApiGamesByGameIdEndError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/end",
  });
};

/**
 * Take back a game's end
 * Appends `GameEndReverted`: the game is no longer marked ended, and its machine is routed to it again unless another game has taken the machine since. The recovery path for a wrong end. A game needs an end time ahead of it to stay in play (the end-time timer ends a game once its end has passed), so the un-end also appends a `GameTimesUpdated` with `endTime` set to the body's `endTime` when given, or to 15 minutes from now when the game's end time has passed. A game whose end is still ahead keeps it. The body is optional.
 */
export const postApiGamesByGameIdUnend = <ThrowOnError extends boolean = false>(
  options: Options<PostApiGamesByGameIdUnendData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdUnendResponse,
    PostApiGamesByGameIdUnendError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/unend",
  });
};

/**
 * Swap the home and away clubs
 * A human corrected which club is home (the mobile "Set Home Team" flow). Appends `TeamsSwapped`: the two clubs exchange designations and nothing else. Each club keeps its line-up, its runs, its hitter/pitcher bookmarks and the half it bats in, so on the wire every `home` / `away` key changes hands at once and `teams.<side>.battingHalf` says which club now bats the top (after a mid-game swap, the home club). Inning, half, outs, count and runners do not move. Also marks the teams as validated. Allowed before first pitch, in progress, and after the game has ended. No request body.
 */
export const postApiGamesByGameIdTeamSwap = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PostApiGamesByGameIdTeamSwapData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdTeamSwapResponse,
    PostApiGamesByGameIdTeamSwapError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/team-swap",
  });
};

/**
 * Confirm the home and away clubs as provisioned
 * A human confirmed that home/away are correct. Appends `TeamsValidated`, which only sets `scheduledTeamsValidated` on the game state so clients can stop prompting. No request body.
 */
export const postApiGamesByGameIdTeamsValidated = <
  ThrowOnError extends boolean = false,
>(
  options: Options<PostApiGamesByGameIdTeamsValidatedData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<
    PostApiGamesByGameIdTeamsValidatedResponse,
    PostApiGamesByGameIdTeamsValidatedError,
    ThrowOnError
  >({
    ...options,
    url: "/api/games/{gameId}/teams-validated",
  });
};

/**
 * The machine this service is currently listening to
 */
export const getApiMachine = <ThrowOnError extends boolean = false>(
  options?: Options<unknown, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    GetApiMachineResponse,
    GetApiMachineError,
    ThrowOnError
  >({
    ...options,
    url: "/api/machine",
  });
};
